import React, { useState, useEffect, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { 
  Bold, Italic, List, ListOrdered, Link as LinkIcon, 
  Image as ImageIcon, Undo, Redo, Heading, Copy, Check
} from 'lucide-react';
import { toast } from 'react-toastify';

interface RichTextEditorWithCounterProps {
  value: string;
  onChange: (value: string) => void;
  maxLength?: number;
}

const RichTextEditorWithCounter: React.FC<RichTextEditorWithCounterProps> = ({ 
  value, 
  onChange,
  maxLength = 800
}) => {
  const [internalValue, setInternalValue] = useState(value || '');
  const [charCount, setCharCount] = useState(0);
  const [copied, setCopied] = useState(false);
  const isUpdatingRef = React.useRef(false);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    if (value !== internalValue && !isUpdatingRef.current) {
      setInternalValue(value || '');
    }
  }, [value, internalValue]);
  
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary hover:text-primary-dark underline',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'rounded-lg max-w-full h-auto',
        },
      }),
    ],
    content: internalValue,
    onUpdate: ({ editor }) => {
      const newContent = editor.getHTML();
      setInternalValue(newContent);
      
      // Calcola il conteggio dei caratteri dal testo puro
      const textContent = editor.getText();
      setCharCount(textContent.length);
      
      // Set flag to prevent infinite loops
      isUpdatingRef.current = true;
      
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      // Debounce the update to prevent focus loss
      timeoutRef.current = setTimeout(() => {
        onChange(newContent);
        isUpdatingRef.current = false;
      }, 300);
    },
    onFocus: () => {
      // This prevents TipTap from losing focus
      isUpdatingRef.current = true;
    },
    onBlur: () => {
      isUpdatingRef.current = false;
    }
  }, []);

  // Handle external value changes
  useEffect(() => {
    if (editor && !isUpdatingRef.current && value !== editor.getHTML()) {
      editor.commands.setContent(value || '', false);
      
      // Aggiorna anche il conteggio dei caratteri
      const textContent = editor.getText();
      setCharCount(textContent.length);
    }
  }, [editor, value]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleCopyContent = useCallback(() => {
    if (editor) {
      // Copia il testo puro, non l'HTML
      const textContent = editor.getText();
      navigator.clipboard.writeText(textContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Testo copiato negli appunti');
    }
  }, [editor]);

  if (!editor) {
    return null;
  }

  const ToolbarButton = ({ 
    onClick, 
    active = false,
    children 
  }: { 
    onClick: () => void;
    active?: boolean;
    children: React.ReactNode;
  }) => (
    <button
      type="button"
      onClick={onClick}
      className={`p-2 rounded hover:bg-gray-100 ${
        active ? 'text-primary bg-primary/5' : 'text-gray-600'
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden">
      <div className="border-b border-gray-300 bg-gray-50 p-2 flex flex-wrap gap-1 justify-between">
        <div className="flex flex-wrap gap-1">
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            active={editor.isActive('bold')}
          >
            <Bold className="h-5 w-5" />
          </ToolbarButton>
          
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            active={editor.isActive('italic')}
          >
            <Italic className="h-5 w-5" />
          </ToolbarButton>
          
          <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
          
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            active={editor.isActive('heading', { level: 2 })}
          >
            <Heading className="h-5 w-5" />
          </ToolbarButton>
          
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            active={editor.isActive('bulletList')}
          >
            <List className="h-5 w-5" />
          </ToolbarButton>
          
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            active={editor.isActive('orderedList')}
          >
            <ListOrdered className="h-5 w-5" />
          </ToolbarButton>
          
          <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
          
          <ToolbarButton
            onClick={() => {
              const url = window.prompt('URL:');
              if (url) {
                editor.chain().focus().setLink({ href: url }).run();
              }
            }}
            active={editor.isActive('link')}
          >
            <LinkIcon className="h-5 w-5" />
          </ToolbarButton>
          
          <ToolbarButton
            onClick={() => {
              const url = window.prompt('URL immagine:');
              if (url) {
                editor.chain().focus().setImage({ src: url }).run();
              }
            }}
          >
            <ImageIcon className="h-5 w-5" />
          </ToolbarButton>
          
          <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
          
          <ToolbarButton onClick={() => editor.chain().focus().undo().run()}>
            <Undo className="h-5 w-5" />
          </ToolbarButton>
          
          <ToolbarButton onClick={() => editor.chain().focus().redo().run()}>
            <Redo className="h-5 w-5" />
          </ToolbarButton>
        </div>
        
        <div className="flex items-center">
          <button
            type="button"
            onClick={handleCopyContent}
            className="p-2 rounded hover:bg-gray-100 text-gray-600 flex items-center"
            title="Copia testo"
          >
            {copied ? <Check className="h-5 w-5 text-green-500 mr-1" /> : <Copy className="h-5 w-5 mr-1" />}
            <span className="text-sm">Copia</span>
          </button>
        </div>
      </div>
      
      <EditorContent 
        editor={editor} 
        className="prose max-w-none p-4 min-h-[200px] focus:outline-none"
      />
      
      <div className="border-t border-gray-300 bg-gray-50 p-2 flex justify-between items-center">
        <div className="text-xs text-gray-500">
          Suggerimento: usa Ctrl+B per grassetto, Ctrl+I per corsivo
        </div>
        <div className={`text-xs font-medium ${charCount > maxLength ? 'text-red-500' : 'text-gray-500'}`}>
          {charCount} / {maxLength} caratteri
          {charCount > maxLength && ' (troppo lungo)'}
        </div>
      </div>
    </div>
  );
};

export default React.memo(RichTextEditorWithCounter);